// src/components/support/Branch.jsx
import React, { useEffect, useState } from "react";
import {
  PlayIcon,
  PhotoIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { getBrandThemeClasses } from "../../utils/brandThemeClasses";

const Branch = ({
  branch,
  index = 0,
  themeId = "default",
  themeClasses: themeClassesProp,
  onMediaClick,
}) => {
  if (!branch) return null;

  const themeClasses =
    themeClassesProp || getBrandThemeClasses(themeId);

  // New data structure:
  // - title: left sidebar title
  // - logo: right content area title (displayed as image)
  // - image_list: array with { subtitle, image, image_pdf }
  // - video_list: array with { subtitle, youtube_id }
  const {
    title,
    subtitle,
    logo,
    videoTitle,
    photoTitle,
    installationTitle,
    image_list = [],
    video_list = [],
    installation_list = [],
  } = branch;
  const hasVideos = video_list.length > 0;
  const hasPhotos = image_list.length > 0;
  const hasInstallations = installation_list.length > 0;
  const [activeSection, setActiveSection] = useState(
    hasVideos ? "videos" : hasPhotos ? "photos" : hasInstallations ? "installations" : "",
  );

  useEffect(() => {
    if (hasVideos && !hasPhotos && !hasInstallations) {
      setActiveSection("videos");
      return;
    }
    if (hasPhotos && !hasVideos && !hasInstallations) {
      setActiveSection("photos");
      return;
    }
    if (hasInstallations && !hasVideos && !hasPhotos) {
      setActiveSection("installations");
      return;
    }
    if (!hasVideos && !hasPhotos && !hasInstallations) {
      setActiveSection("");
    }
  }, [hasVideos, hasPhotos, hasInstallations]);

  useEffect(() => {
    if (hasVideos) {
      setActiveSection("videos");
      return;
    }
    if (hasPhotos) {
      setActiveSection("photos");
      return;
    }
    if (hasInstallations) {
      setActiveSection("installations");
      return;
    }
    setActiveSection("");
  }, [branch.id, hasVideos, hasPhotos, hasInstallations]);

  const scrollToSection = (section) => {
    setActiveSection(section);
  };

  return (
    <div
      style={{
        animationDelay: `${index * 150}ms`,
      }}
    >
      {(hasVideos || hasPhotos || hasInstallations) && (
        <div
          className="relative z-10 mb-0 flex flex-wrap items-end gap-0.5 sm:gap-1 pl-1 pr-1 sm:pl-2 sm:pr-2 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-0 after:h-px after:bg-mist-200/70"
          role="tablist"
          aria-label="Support content sections"
        >
            {hasVideos && (
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === "videos"}
                className={`inline-flex min-w-[4.5rem] items-center gap-2 whitespace-nowrap text-sm sm:text-[0.9375rem] tracking-tight transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400/80 ${
                  activeSection === "videos"
                    ? themeClasses.tabActive
                    : `${themeClasses.tabInactive} hover:-translate-y-0.5`
                }`}
                onClick={() => scrollToSection("videos")}
              >
                <PlayIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                {videoTitle || "Videos"}
              </button>
            )}
            {hasPhotos && (
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === "photos"}
                className={`inline-flex min-w-[4.5rem] items-center gap-2 whitespace-nowrap text-sm sm:text-[0.9375rem] tracking-tight transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400/80 ${
                  activeSection === "photos"
                    ? themeClasses.tabActive
                    : `${themeClasses.tabInactive} hover:-translate-y-0.5`
                }`}
                onClick={() => scrollToSection("photos")}
              >
                <PhotoIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                {photoTitle || "Photos"}
              </button>
            )}
            {hasInstallations && (
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === "installations"}
                className={`inline-flex min-w-[4.5rem] items-center gap-2 whitespace-nowrap text-sm sm:text-[0.9375rem] tracking-tight transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400/80 ${
                  activeSection === "installations"
                    ? themeClasses.tabActive
                    : `${themeClasses.tabInactive} hover:-translate-y-0.5`
                }`}
                onClick={() => scrollToSection("installations")}
              >
                <WrenchScrewdriverIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                {installationTitle || "Installation"}
              </button>
            )}
        </div>
      )}

      <div
        className="product-panel-elevated rounded-2xl p-6 shadow-xl backdrop-blur-md transition-all duration-300 hover:shadow-2xl md:p-8 lg:p-10"
        role="tabpanel"
      >
      {/* Branch Header: left title, right logo (like products brand header) */}
      {(title || (logo && logo.url)) && (
        <div className="mb-6 md:mb-8 flex items-center justify-between gap-4">
          <div>
            {title && (
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-1 text-sm sm:text-base text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
          {logo && logo.url && (
            <div className="ml-4 flex-shrink-0 flex items-center justify-center">
              <img
                src={`${logo.url}`}
                alt={logo.alternativeText || logo.name || "Brand Logo"}
                className="h-12 sm:h-14 md:h-16 w-auto object-contain"
              />
            </div>
          )}
        </div>
      )}

      <div key={`${branch.id || "branch"}-${activeSection}`} className="support-tab-fade-in">
        {/* Videos Section */}
        {hasVideos && activeSection === "videos" && (
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-2 mb-4 md:mb-6">
              <PlayIcon className="h-6 w-6 text-[color:var(--pt-500)]" />
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800">
                {videoTitle || "Videos"}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full">
              {video_list.map((video, videoIndex) => {
                const youtubeId = video.youtube_id || video.youtubeId;
                const videoSubtitle = video.subtitle || video.videoTitle || `Video ${videoIndex + 1}`;

                if (!youtubeId) return null;

                return (
                  <button
                    key={video.id || videoIndex}
                    type="button"
                    className="text-left focus:outline-none group flex flex-col h-full support-media-card-fade-in"
                    style={{ "--support-media-delay": `${videoIndex * 50}ms` }}
                    onClick={() =>
                      onMediaClick &&
                      onMediaClick({
                        type: "video",
                        title: videoSubtitle,
                        youtubeId,
                      })
                    }
                  >
                    <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-300">
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeId}`}
                        title={videoSubtitle}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <PlayIcon className="h-12 w-12 text-white/80" />
                      </div>
                    </div>
                    <div className="mt-2 flex-grow">
                      <p
                        className="mt-2 line-clamp-2 text-center text-sm font-medium text-gray-900 group-hover:text-[color:var(--pt-600)] md:text-base"
                      >
                        {videoSubtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Photos Section */}
        {hasPhotos && activeSection === "photos" && (
          <div>
            <div className="flex items-center gap-2 mb-4 md:mb-6">
              <PhotoIcon className="h-6 w-6 text-[color:var(--pt-500)]" />
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800">
                {photoTitle || "Photos"}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full">
              {image_list.map((item, photoIndex) => {
                const image = item.image || item;
                const imageUrl = item.image?.url;
                if (!imageUrl) return null;

                const photoSubtitle = item.subtitle || item.photoTitle || image?.alternativeText || `Photo ${photoIndex + 1}`;

                // Check if image_pdf exists
                const imagePdf = item.image_pdf;
                const pdfUrl = imagePdf?.url;

                // If PDF exists, open PDF on click; otherwise show in modal
                const handleImageClick = () => {
                  if (pdfUrl) {
                    // Open PDF in new tab
                    window.open(pdfUrl, "_blank", "noopener,noreferrer");
                  } else {
                    // Show image in modal
                    onMediaClick &&
                      onMediaClick({
                        type: "photo",
                        title: photoSubtitle,
                        imageUrl: imageUrl,
                      });
                  }
                };

                return (
                  <button
                    key={item.id || image?.id || photoIndex}
                    type="button"
                    className="text-left focus:outline-none group flex flex-col h-full support-media-card-fade-in"
                    style={{ "--support-media-delay": `${photoIndex * 50}ms` }}
                    onClick={handleImageClick}
                  >
                    <div className="relative w-full aspect-video overflow-hidden rounded-xl shadow-lg group-hover:shadow-2xl transition-all duration-300">
                      <img
                        src={imageUrl}
                        alt={image?.alternativeText || photoSubtitle || "Support"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {pdfUrl && (
                        <div
                          className="absolute top-2 right-2 rounded bg-[color:var(--pt-600)] px-2 py-1 text-xs font-semibold text-white"
                        >
                          PDF
                        </div>
                      )}
                    </div>
                    <p className="mt-2 line-clamp-2 min-h-[3.25rem] text-center text-sm font-medium text-gray-900 group-hover:text-[color:var(--pt-600)] md:min-h-[3.5rem] md:text-base">
                      {photoSubtitle}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Installation Section */}
        {hasInstallations && activeSection === "installations" && (
          <div>
            <div className="flex items-center gap-2 mb-4 md:mb-6">
              <WrenchScrewdriverIcon className="h-6 w-6 text-[color:var(--pt-500)]" />
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800">
                {installationTitle || "Installation"}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full">
              {installation_list.map((item, installIndex) => {
                const image = item.image || item;
                const imageUrl = item.image?.url;
                if (!imageUrl) return null;

                const installSubtitle =
                  item.subtitle ||
                  image?.alternativeText ||
                  `Installation ${installIndex + 1}`;

                const pdfUrl = item.image_pdf?.url;

                const handleInstallationClick = () => {
                  if (pdfUrl) {
                    window.open(pdfUrl, "_blank", "noopener,noreferrer");
                    return;
                  }
                  onMediaClick &&
                    onMediaClick({
                      type: "photo",
                      title: installSubtitle,
                      imageUrl,
                    });
                };

                return (
                  <button
                    key={item.id || image?.id || installIndex}
                    type="button"
                    className="text-left focus:outline-none group flex flex-col h-full support-media-card-fade-in"
                    style={{ "--support-media-delay": `${installIndex * 50}ms` }}
                    onClick={handleInstallationClick}
                  >
                    <div className="relative w-full aspect-video overflow-hidden rounded-xl shadow-lg group-hover:shadow-2xl transition-all duration-300">
                      <img
                        src={imageUrl}
                        alt={image?.alternativeText || installSubtitle || "Installation"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {pdfUrl && (
                        <div
                          className="absolute top-2 right-2 rounded bg-[color:var(--pt-600)] px-2 py-1 text-xs font-semibold text-white"
                        >
                          PDF
                        </div>
                      )}
                    </div>
                    <p className="mt-2 line-clamp-2 min-h-[3.25rem] text-center text-sm font-medium text-gray-900 group-hover:text-[color:var(--pt-600)] md:min-h-[3.5rem] md:text-base">
                      {installSubtitle}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {(!video_list || video_list.length === 0) &&
        (!image_list || image_list.length === 0) &&
        (!installation_list || installation_list.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            <p>No content available for this brand.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Branch;

